import { useRef, useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Images, Pencil, Search, Trash2, Upload } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VideotecaPanel } from "@/pages/VideotecaPanel";
import { api, errMsg, fetcher } from "@/lib/api";

export default function Biblioteca() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [uploadCat, setUploadCat] = useState("Fachadas");
  const [caption, setCaption] = useState("");
  const [credit, setCredit] = useState("");
  const [tags, setTags] = useState("");
  const [altText, setAltText] = useState("");
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [tab, setTab] = useState("imagenes");
  const fileRef = useRef(null);

  const key = `/library?q=${encodeURIComponent(q)}&category=${encodeURIComponent(category)}&limit=60`;
  const { data, mutate } = useSWR(key, fetcher);
  const { data: cats } = useSWR("/library/categories", fetcher);
  const categories = cats?.categories || [];

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const form = new FormData();
    [...files].forEach((f) => form.append("files", f));
    form.append("category", uploadCat);
    form.append("caption", caption);
    form.append("credit", credit);
    form.append("tags", tags);
    form.append("alt_text", altText);
    form.append("description", description);
    form.append("title", title);
    try {
      const { data: r } = await api.post("/library", form);
      toast.success(`${r.items.length} imagen(es) guardadas`);
      (r.errors || []).forEach((e) => toast.error(`${e.file}: ${e.error}`));
      setCaption("");
      setTags("");
      setTitle("");
      setAltText("");
      setDescription("");
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    try {
      await api.put(`/library/${edit.id}`, {
        name: edit.name, title: edit.title, alt_text: edit.alt_text,
        caption: edit.caption, description: edit.description, credit: edit.credit,
        category: edit.category, tags: (edit.tagsText || "").split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Imagen actualizada");
      setEdit(null);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Quitar «${item.name}» del repositorio?`)) return;
    try {
      await api.delete(`/library/${item.id}`);
      toast.success("Imagen eliminada del repositorio");
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="biblioteca-page">
      <PageHeader
        kicker="Repositorio"
        title="Biblioteca de imágenes y videos"
        description="Archivo con los datos que pide WordPress/JNews: título, texto alternativo, leyenda, descripción, crédito y categoría."
      >
        {tab === "imagenes" && (
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="library-upload-btn">
            <Upload className="h-4 w-4 mr-2" />{busy ? "Subiendo…" : "Subir imágenes"}
          </Button>
        )}
      </PageHeader>

      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
             onChange={(e) => upload(e.target.files)} data-testid="library-file-input" />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="imagenes" data-testid="tab-biblioteca-imagenes">Imágenes</TabsTrigger>
          <TabsTrigger value="videos" data-testid="tab-biblioteca-videos">Videos</TabsTrigger>
        </TabsList>
        <TabsContent value="videos"><VideotecaPanel /></TabsContent>
        <TabsContent value="imagenes">

      <div className="border border-border bg-card p-5 mb-6 grid md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider">Categoría al subir</Label>
          <select value={uploadCat} onChange={(e) => setUploadCat(e.target.value)}
                  className="w-full mt-1.5 h-10 border border-input bg-background px-3 text-sm rounded-md"
                  data-testid="library-upload-category">
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Título (WordPress)</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5"
                 data-testid="library-upload-title" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Pie de foto</Label>
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} className="mt-1.5"
                 data-testid="library-upload-caption" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Crédito</Label>
          <Input value={credit} onChange={(e) => setCredit(e.target.value)} className="mt-1.5"
                 data-testid="library-upload-credit" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Etiquetas (separadas por coma)</Label>
          <Input value={tags} onChange={(e) => setTags(e.target.value)} className="mt-1.5"
                 data-testid="library-upload-tags" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Texto alternativo (alt)</Label>
          <Input value={altText} onChange={(e) => setAltText(e.target.value)} className="mt-1.5"
                 data-testid="library-upload-alt" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Descripción (WordPress)</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1.5"
                 data-testid="library-upload-description" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, pie o etiqueta"
                 className="pl-9" data-testid="library-search-input" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="h-10 border border-input bg-background px-3 text-sm rounded-md"
                data-testid="library-filter-category">
          <option value="">Todas las categorías</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-muted-foreground" data-testid="library-total">
          {data?.total || 0} imágenes
        </span>
      </div>

      {!data?.items?.length ? (
        <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground"
             data-testid="library-empty">
          <Images className="h-6 w-6 mx-auto mb-3 opacity-60" />
          Todavía no hay imágenes en el repositorio.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="library-grid">
          {data.items.map((it) => (
            <div key={it.id} className="border border-border bg-card group" data-testid={`library-item-${it.id}`}>
              <img src={it.url} alt={it.caption || it.name}
                   className="w-full aspect-[4/3] object-cover bg-muted" />
              <div className="p-3">
                <p className="text-sm font-medium truncate" title={it.name}>{it.name}</p>
                <p className="text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">{it.caption}</p>
                <div className="flex items-center justify-between mt-2">
                  <Badge variant="secondary" className="text-[10px]">{it.category}</Badge>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7"
                            onClick={() => setEdit({ ...it, tagsText: (it.tags || []).join(", ") })}
                            data-testid={`library-edit-${it.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive"
                            onClick={() => remove(it)} data-testid={`library-delete-${it.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

        </TabsContent>
      </Tabs>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg" data-testid="library-edit-dialog">
          <DialogHeader><DialogTitle>Datos de la imagen</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <img src={edit.url} alt="" className="w-full max-h-56 object-contain bg-muted" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nombre del archivo</Label>
                  <Input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                         className="mt-1.5" data-testid="library-edit-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Título (WordPress)</Label>
                  <Input value={edit.title || ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                         className="mt-1.5" data-testid="library-edit-title" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Texto alternativo (alt)</Label>
                <Input value={edit.alt_text || ""} onChange={(e) => setEdit({ ...edit, alt_text: e.target.value })}
                       className="mt-1.5" data-testid="library-edit-alt" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Pie de foto</Label>
                <Textarea value={edit.caption || ""} rows={2}
                          onChange={(e) => setEdit({ ...edit, caption: e.target.value })}
                          className="mt-1.5" data-testid="library-edit-caption" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Crédito</Label>
                  <Input value={edit.credit || ""} onChange={(e) => setEdit({ ...edit, credit: e.target.value })}
                         className="mt-1.5" data-testid="library-edit-credit" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Categoría</Label>
                  <select value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })}
                          className="w-full mt-1.5 h-10 border border-input bg-background px-3 text-sm rounded-md"
                          data-testid="library-edit-category">
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Descripción (WordPress)</Label>
                <Textarea value={edit.description || ""} rows={2}
                          onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                          className="mt-1.5" data-testid="library-edit-description" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Etiquetas</Label>
                <Input value={edit.tagsText || ""} onChange={(e) => setEdit({ ...edit, tagsText: e.target.value })}
                       className="mt-1.5" data-testid="library-edit-tags" />
              </div>
              <Button onClick={save} className="w-full" data-testid="library-edit-save">Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
